import { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IssueEnvironment } from '../../api/issues';
import { age, dateAgo, day, environmentKey, moment } from './issueFormat';

export interface IIssueOverviewTableProps {
    environments: IssueEnvironment[];
    selected: string | null;
    sweeping: string | null;
    onSelect: (environment: IssueEnvironment | null) => void;
    onSweep: (environment: IssueEnvironment) => void;
}

type SortKey =
    | 'name'
    | 'open'
    | 'waitingOnCustomer'
    | 'customerNotTold'
    | 'waitingOnUs'
    | 'needsRequeue'
    | 'resolvesItself'
    | 'proposalsWaiting'
    | 'resolved'
    | 'oldestOpenAt'
    | 'latestOpenFailureAt'
    | 'exactWaiting'
    | 'lastSuccessAt';

interface Column {
    key: SortKey;
    label: string;
    title: string;
    numeric?: boolean;
}

/** In the order they stand; what is open first, then what was solved, then time. */
const COLUMNS: Column[] = [
    { key: 'name', label: 'Klant / omgeving', title: 'Sorteer op naam' },
    { key: 'open', label: 'Open', title: 'Alle open issues', numeric: true },
    { key: 'waitingOnCustomer', label: 'Klant', title: 'Wacht op een aanpassing door de klant', numeric: true },
    { key: 'customerNotTold', label: 'Niet gemeld', title: 'De klant weet hier nog niets van', numeric: true },
    { key: 'waitingOnUs', label: 'Wij', title: 'Wacht op ons: de koppeling, de instellingen of de code', numeric: true },
    { key: 'needsRequeue', label: 'Opnieuw', title: 'De connector probeert niet meer; opnieuw aanbieden', numeric: true },
    { key: 'resolvesItself', label: 'Vanzelf', title: 'Wordt nog vanzelf opnieuw geprobeerd', numeric: true },
    { key: 'proposalsWaiting', label: 'Voorstellen', title: 'Voorstellen om opnieuw aan te bieden die op goedkeuring wachten', numeric: true },
    { key: 'resolved', label: 'Opgelost', title: 'Opgeloste en gesloten issues in de backlog', numeric: true },
    { key: 'oldestOpenAt', label: 'Oudste open', title: 'Hoe lang het oudste open issue al openstaat' },
    { key: 'latestOpenFailureAt', label: 'Laatste fout', title: 'Wanneer een open issue voor het laatst misging: speelt het nog?' },
    {
        key: 'exactWaiting',
        label: 'Naar Exact',
        title: 'Berichten die bij de laatste scan op de connector wachtten om naar Exact te gaan, en wat er vandaag nog over is van de Exact-daglimiet',
        numeric: true,
    },
    {
        key: 'lastSuccessAt',
        label: 'Laatste scan',
        title: 'Wanneer de scanner deze omgeving voor het laatst las, en wat die scan nieuw vond en zag oplossen',
    },
];

const timeOf = (value?: string | null) => (value ? new Date(value).getTime() : null);

const valueOf = (e: IssueEnvironment, key: SortKey): string | number | null => {
    switch (key) {
        case 'name':
            return environmentKey(e.customer, e.environment);
        // Without open issues there is no oldest or latest one; those sort last.
        case 'oldestOpenAt':
            return e.open > 0 ? timeOf(e.oldestOpenAt) : null;
        case 'latestOpenFailureAt':
            return e.open > 0 ? timeOf(e.latestOpenFailureAt) : null;
        case 'lastSuccessAt':
            return timeOf(e.lastSuccessAt);
        case 'exactWaiting':
            return e.exactWaiting ?? null;
        default:
            return e[key];
    }
};

/** Most first for counts and the newest first for times, apart from the oldest open issue. */
const firstDirection = (key: SortKey): 'asc' | 'desc' => (key === 'name' || key === 'oldestOpenAt' ? 'asc' : 'desc');

/**
 * What waits for Exact and how much of the day's limit is left. Many waiting with little left
 * is the start-of-month crunch: those messages will hit the limit and come back as issues.
 */
const ExactLoad = ({ environment: e }: { environment: IssueEnvironment }) => {
    if (e.exactWaiting == null && e.exactRemaining == null) {
        return <td className="issues__num issues__zero">–</td>;
    }

    const tight = e.exactRemaining != null && e.exactLimit ? e.exactRemaining < e.exactLimit * 0.1 : false;
    const title = [
        e.exactWaiting != null ? `${e.exactWaiting} wachten op de connector` : 'wachtrij niet gelezen',
        e.exactPoison ? `${e.exactPoison} in poison (vijf keer mislukt)` : null,
        e.exactRemaining != null ? `${e.exactRemaining}${e.exactLimit ? ` van ${e.exactLimit}` : ''} Exact-calls over vandaag` : 'daglimiet niet bekend',
        e.exactResetAt ? `de limiet begint opnieuw ${moment(e.exactResetAt)}` : null,
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        <td className={`issues__num${tight ? ' issues__warn' : ''}`} title={title}>
            {e.exactWaiting ?? '?'}
            {e.exactPoison ? <span className="issues__muted"> +{e.exactPoison}</span> : null}
            {e.exactRemaining != null && <span className="issues__muted"> · {e.exactRemaining} over</span>}
        </td>
    );
};

const Count = ({ value, strong }: { value: number; strong?: boolean }) => (
    <td className="issues__num">
        {value === 0 ? (
            <span className="issues__zero">0</span>
        ) : strong ? (
            <strong>{value}</strong>
        ) : (
            value
        )}
    </td>
);

/**
 * Every environment of every customer on one line: how much is open, whose move it is, how
 * old the oldest open issue is, whether it still goes wrong, and whether the scanner keeps
 * up. What replaces clicking through every customer in the morning. Every column sorts.
 */
export const IssueOverviewTable = ({ environments, selected, sweeping, onSelect, onSweep }: IIssueOverviewTableProps) => {
    const [sort, setSort] = useState<{ key: SortKey; direction: 'asc' | 'desc' } | null>(null);

    const rows = useMemo(() => {
        if (!sort) {
            return environments;
        }

        const factor = sort.direction === 'asc' ? 1 : -1;

        return [...environments].sort((a, b) => {
            const left = valueOf(a, sort.key);
            const right = valueOf(b, sort.key);

            // Empty stays at the bottom whichever way round.
            if (left === null || right === null) {
                return left === right ? 0 : left === null ? 1 : -1;
            }

            const order = typeof left === 'string' ? left.localeCompare(String(right)) : left - (right as number);

            return order * factor || environmentKey(a.customer, a.environment).localeCompare(environmentKey(b.customer, b.environment));
        });
    }, [environments, sort]);

    const sortBy = (key: SortKey) =>
        setSort((current) =>
            current?.key === key
                ? current.direction === firstDirection(key)
                    ? { key, direction: firstDirection(key) === 'asc' ? 'desc' : 'asc' }
                    : null
                : { key, direction: firstDirection(key) }
        );

    return (
        <div className="issues__scroll">
            <table className="issues__table">
                <thead>
                    <tr>
                        {COLUMNS.map((column) => {
                            const active = sort?.key === column.key;

                            return (
                                <th
                                    key={column.key}
                                    className={column.numeric ? 'issues__num' : undefined}
                                    aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                                >
                                    <button type="button" className="issues__sort" title={column.title} onClick={() => sortBy(column.key)}>
                                        {column.label}
                                        <FontAwesomeIcon
                                            icon={active ? (sort.direction === 'asc' ? 'sort-up' : 'sort-down') : 'sort'}
                                            className={active ? undefined : 'issues__sort-idle'}
                                        />
                                    </button>
                                </th>
                            );
                        })}
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {rows.map((e) => {
                        const key = environmentKey(e.customer, e.environment);
                        const isSelected = key === selected;

                        return (
                            <tr
                                key={key}
                                className={`is-clickable${isSelected ? ' is-selected' : ''}`}
                                onClick={() => onSelect(isSelected ? null : e)}
                            >
                                <td>
                                    <span className="issues__env-name" title="Toon de issues van deze omgeving">
                                        {e.customer} / {e.environment}
                                    </span>
                                    <a
                                        className="issues__log-link"
                                        href={`/dashboard/${encodeURIComponent(e.customer)}/${encodeURIComponent(e.environment)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(event) => event.stopPropagation()}
                                        title="Foutenlogboek van deze omgeving, in een nieuw tabblad"
                                        aria-label="Foutenlogboek"
                                    >
                                        <FontAwesomeIcon icon="arrow-up-right-from-square" />
                                    </a>
                                </td>
                                <Count value={e.open} strong />
                                <Count value={e.waitingOnCustomer} />
                                <Count value={e.customerNotTold} strong />
                                <Count value={e.waitingOnUs} strong />
                                <Count value={e.needsRequeue} />
                                <Count value={e.resolvesItself} />
                                <Count value={e.proposalsWaiting} strong />
                                <Count value={e.resolved} />
                                <td title={e.oldestOpenAt ?? undefined}>{e.open > 0 ? age(e.oldestOpenAt) : '–'}</td>
                                <td title={e.latestOpenFailureAt ?? undefined}>{e.open > 0 ? dateAgo(e.latestOpenFailureAt) : '–'}</td>
                                <ExactLoad environment={e} />
                                <td>
                                    <LastScan environment={e} />
                                </td>
                                <td onClick={(event) => event.stopPropagation()} className="issues__row-actions">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        disabled={sweeping !== null}
                                        onClick={() => onSweep(e)}
                                        title="Deze omgeving nu scannen in plaats van op de volgende ronde wachten"
                                    >
                                        <FontAwesomeIcon icon="rotate" spin={sweeping === key} />
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

/**
 * When the scanner last read the environment, and what that scan changed: one fact per line.
 * While the year of history is still being read, it says how far it got.
 */
const LastScan = ({ environment: e }: { environment: IssueEnvironment }) => {
    if (e.lastError) {
        return (
            <span className="text-danger" title={e.lastError}>
                <FontAwesomeIcon icon="triangle-exclamation" /> mislukt {moment(e.lastRunAt)}
            </span>
        );
    }

    if (!e.swept) {
        return <span className="issues__muted">nog niet gescand</span>;
    }

    return (
        <span className="issues__scan">
            <span>{moment(e.lastSuccessAt)}</span>
            {!e.backfillDone && (
                <span
                    className="issues__muted"
                    title="De eerste scans lezen het afgelopen jaar in, een paar maanden per ronde. Tot dat klaar is, mist de backlog oudere issues."
                >
                    historie t/m {day(e.watermark)}
                </span>
            )}
            {e.lastFound > 0 && <span className="issues__type">+{e.lastFound} nieuw</span>}
            {e.lastResolved > 0 && <span className="issues__type">{e.lastResolved} opgelost</span>}
            {(e.neverArrivedAt || e.neverArrivedNote) && (
                <span
                    className="issues__muted"
                    title={
                        e.neverArrivedNote ??
                        'Eén keer per dag vergelijkt de scanner Chargebee met de koppelingen, om objecten te vinden die nooit bij de connector aankwamen.'
                    }
                >
                    Chargebee vergeleken {e.neverArrivedAt ? moment(e.neverArrivedAt) : 'nog niet'}
                    {e.neverArrivedOpen ? `, ${e.neverArrivedOpen} nooit aangekomen` : ''}
                    {e.neverArrivedNote ? ' (nog niet bij)' : ''}
                </span>
            )}
            <Poison environment={e} />
        </span>
    );
};

/**
 * What the last scan found in the poison queues: how many can go, and whether it deleted them or only says so.
 * The breakdown per queue and reason is in the tooltip.
 */
const Poison = ({ environment: e }: { environment: IssueEnvironment }) => {
    const r = e.poisonReport;

    if (!r || (r.read === 0 && !r.error)) {
        return null;
    }

    const title = [
        ...r.groups.map((g) => `${g.queue}: ${g.label} ${g.count}${g.deletes ? (r.deleting ? ' (gewist)' : ' (kan weg)') : ''}`),
        r.more ? 'Meer dan één scan leest; de volgende ronde gaat verder.' : null,
        r.error ? `Fout: ${r.error}` : null,
    ]
        .filter(Boolean)
        .join('\n');
    const kept = r.read - (r.deleting ? r.deleted : r.canGo);

    return (
        <span className={r.error ? 'text-danger' : 'issues__muted'} title={title}>
            Poison {r.read}
            {r.more ? '+' : ''}: {r.deleting ? `${r.deleted} gewist` : `${r.canGo} kan weg (proef)`}, {kept} blijven
        </span>
    );
};
